'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '@/lib/stripe/client';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

interface CreditPackage {
  id: string;
  name: string;
  credits: number;
  price: string;
  priceValue: number;
  popular: boolean;
  description: string;
  color: string;
  icon: string;
}

const PACKAGES: CreditPackage[] = [
  {
    id: 'starter',
    name: 'Starter',
    credits: 500,
    price: '4,99',
    priceValue: 499,
    popular: false,
    description: 'Ideal para experimentar e criar os primeiros jogos.',
    color: 'text-muted-foreground',
    icon: 'BoltIcon',
  },
  {
    id: 'creator',
    name: 'Creator',
    credits: 1500,
    price: '11,99',
    priceValue: 1199,
    popular: true,
    description: 'O mais popular. Perfeito para criadores regulares.',
    color: 'text-primary',
    icon: 'SparklesIcon',
  },
  {
    id: 'pro',
    name: 'Pro',
    credits: 5000,
    price: '34,99',
    priceValue: 3499,
    popular: false,
    description: 'Para criadores sérios com projetos ambiciosos.',
    color: 'text-accent',
    icon: 'RocketLaunchIcon',
  },
  {
    id: 'ultimate',
    name: 'Ultimate',
    credits: 15000,
    price: '89,99',
    priceValue: 8999,
    popular: false,
    description: 'Máximo poder para estúdios e profissionais.',
    color: 'text-warning',
    icon: 'TrophyIcon',
  },
];

interface PaymentFormProps {
  clientSecret: string;
  selectedPackage: CreditPackage;
  onSuccess: () => void;
  onCancel: () => void;
}

function PaymentForm({ clientSecret, selectedPackage, onSuccess, onCancel }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);
    setErrorMsg(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${process.env.NEXT_PUBLIC_SITE_URL || (typeof window !== 'undefined' ? window.location.origin : '')}/credits?payment=success`,
      },
      redirect: 'if_required',
    });

    if (error) {
      setErrorMsg(error.message || 'Pagamento falhou. Tenta novamente.');
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === 'succeeded') {
      onSuccess();
    }
    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="p-4 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-foreground">Pacote {selectedPackage.name}</p>
          <p className="text-xs text-muted-foreground">{selectedPackage.credits.toLocaleString('pt-PT')} créditos</p>
        </div>
        <p className="text-xl font-bold text-foreground">€{selectedPackage.price}</p>
      </div>

      <PaymentElement />

      {errorMsg && (
        <div className="p-3 rounded-lg bg-danger/10 border border-danger/20 text-xs text-danger flex items-center gap-2">
          <Icon name="ExclamationTriangleIcon" size={14} />
          {errorMsg}
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="button"
          onClick={onCancel}
          disabled={isProcessing}
          className="flex-1 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/50 border border-border transition-all duration-150 disabled:opacity-50"
        >
          Voltar
        </button>
        <button
          type="submit"
          disabled={!stripe || isProcessing}
          className="flex-1 flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90 active:scale-95 transition-all duration-150 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <>
              <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
              A processar...
            </>
          ) : (
            <>
              <Icon name="LockClosedIcon" size={16} />
              Pagar €{selectedPackage.price}
            </>
          )}
        </button>
      </div>
      <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
        <Icon name="LockClosedIcon" size={11} />
        Pagamento seguro via Stripe
      </p>
    </form>
  );
}

export default function CreditsPage() {
  const { user, credits, refreshCredits } = useAuth();
  const router = useRouter();
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const stripePromise = getStripe();

  const dailyRemaining = (credits?.daily_credits_total ?? 75) - (credits?.daily_credits_used ?? 0);
  const purchasedCredits = credits?.purchased_credits ?? 0;

  const handleSelectPackage = async (pkg: CreditPackage) => {
    if (!user) return;
    setSelectedPackage(pkg);
    setIsLoading(true);
    setError(null);

    try {
      const supabase = createClient();
      const { data, error: fnError } = await supabase.functions.invoke<{
        clientSecret: string;
        paymentIntentId: string;
      }>('create-payment-intent', {
        body: {
          packageId: pkg.id,
          userId: user.id,
          userEmail: user.email,
        },
      });

      if (fnError || !data?.clientSecret) {
        throw new Error((data as { error?: string })?.error ?? fnError?.message ?? 'Erro ao iniciar pagamento');
      }

      setClientSecret(data.clientSecret);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao iniciar pagamento';
      setError(message);
      setSelectedPackage(null);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = async () => {
    setPaymentSuccess(true);
    setTimeout(async () => {
      await refreshCredits();
    }, 2000);
  };

  const handleBack = () => {
    setSelectedPackage(null);
    setClientSecret(null);
    setError(null);
  };

  if (paymentSuccess) {
    return (
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
        <div className="max-w-md mx-auto mt-16 text-center">
          <div className="w-20 h-20 rounded-full bg-success/15 flex items-center justify-center mx-auto mb-6">
            <Icon name="CheckCircleIcon" size={44} className="text-success" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-3">Pagamento confirmado!</h2>
          <p className="text-sm text-muted-foreground mb-8">
            Os teus créditos serão adicionados em instantes. Podes começar a criar jogos agora.
          </p>
          <button
            onClick={() => router.push('/create-game')}
            className="w-full py-3 rounded-xl text-sm font-semibold text-white gradient-primary hover:opacity-90 transition-all duration-150"
          >
            Criar jogo agora
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-lg bg-warning/15 flex items-center justify-center">
            <Icon name="BoltIcon" size={16} className="text-warning" variant="solid" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Créditos</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Compra créditos para gerar jogos com modelos avançados de IA.
        </p>
      </div>

      {/* Current balance */}
      <div className="grid grid-cols-2 gap-4 mb-8 max-w-sm">
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs text-muted-foreground mb-1">Créditos diários</p>
          <p className="text-2xl font-black text-foreground tabular-nums">{dailyRemaining}</p>
          <p className="text-xs text-muted-foreground">restantes hoje</p>
        </div>
        <div className="p-4 rounded-xl bg-card border border-border">
          <p className="text-xs text-muted-foreground mb-1">Créditos comprados</p>
          <p className="text-2xl font-black text-foreground tabular-nums">{purchasedCredits.toLocaleString('pt-PT')}</p>
          <p className="text-xs text-muted-foreground">nunca expiram</p>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-3 rounded-lg bg-danger/10 border border-danger/20 text-sm text-danger flex items-center gap-2 max-w-2xl">
          <Icon name="ExclamationTriangleIcon" size={16} />
          {error}
        </div>
      )}

      {/* Payment form or package selection */}
      {selectedPackage && clientSecret ? (
        <div className="max-w-md">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          >
            <Icon name="ArrowLeftIcon" size={16} />
            Voltar aos pacotes
          </button>
          <div className="bg-card border border-border rounded-2xl p-6">
            <h2 className="text-base font-bold text-foreground mb-5">Finalizar compra</h2>
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <PaymentForm
                clientSecret={clientSecret}
                selectedPackage={selectedPackage}
                onSuccess={handlePaymentSuccess}
                onCancel={handleBack}
              />
            </Elements>
          </div>
        </div>
      ) : (
        <>
          <h2 className="text-base font-semibold text-foreground mb-4">Escolhe um pacote</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 max-w-5xl">
            {PACKAGES.map((pkg) => (
              <div
                key={pkg.id}
                className={`relative flex flex-col p-5 rounded-2xl border transition-all duration-200 hover:scale-[1.02] ${
                  pkg.popular
                    ? 'border-primary/50 bg-primary/5 shadow-lg shadow-primary/10'
                    : 'border-border bg-card hover:border-border/80'
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 rounded-full text-xs font-bold text-white gradient-primary shadow-sm">
                      Mais popular
                    </span>
                  </div>
                )}

                <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${
                  pkg.popular ? 'bg-primary/20' : 'bg-muted'
                }`}>
                  <Icon
                    name={pkg.icon as Parameters<typeof Icon>[0]['name']}
                    size={20}
                    className={pkg.popular ? 'text-primary' : 'text-muted-foreground'}
                    variant="solid"
                  />
                </div>

                <h3 className="text-base font-bold text-foreground mb-1">{pkg.name}</h3>
                <p className="text-xs text-muted-foreground mb-4 flex-1">{pkg.description}</p>

                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-black text-foreground">€{pkg.price}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    <span className="font-semibold text-foreground">{pkg.credits.toLocaleString('pt-PT')}</span> créditos
                  </p>
                  <p className="text-xs text-muted-foreground">
                    ≈ €{(parseFloat(pkg.price.replace(',', '.')) / pkg.credits * 100).toFixed(3)} por crédito
                  </p>
                </div>

                <button
                  onClick={() => handleSelectPackage(pkg)}
                  disabled={isLoading || !user}
                  className={`w-full py-2.5 rounded-xl text-sm font-semibold transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed ${
                    pkg.popular
                      ? 'text-white gradient-primary hover:opacity-90' :'text-foreground bg-muted hover:bg-muted/80 border border-border'
                  }`}
                >
                  {isLoading && selectedPackage?.id === pkg.id ? (
                    <span className="flex items-center justify-center gap-2">
                      <Icon name="ArrowPathIcon" size={14} className="animate-spin" />
                      A carregar...
                    </span>
                  ) : (
                    'Comprar agora'
                  )}
                </button>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 rounded-xl bg-muted/30 border border-border max-w-5xl">
            <div className="flex items-start gap-3">
              <Icon name="InformationCircleIcon" size={18} className="text-muted-foreground flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground mb-1">Sobre os créditos</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Os créditos comprados <strong className="text-foreground">nunca expiram</strong> e são usados após os créditos diários gratuitos.</li>
                  <li>• Cada geração consome créditos conforme o modelo escolhido (Free: 5, Pro: 15, Advanced: 30).</li>
                  <li>• Pagamento seguro processado via Stripe. Não guardamos dados de cartão.</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
